/**
 * SharedListsPage
 *
 * Index page at /app/shared-lists. Shows all lists the user is a member of.
 * Each entry shows name, member count, and the user's role.
 * Includes a "Create list" button with an inline input.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Loader2, ChevronRight } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { getSharedLists, createSharedList } from '../../api/shared-lists';
import type { SharedListWithRole } from '../../api/shared-lists';

export function SharedListsPage() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<SharedListWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadLists = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getSharedLists();
      setLists(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shared lists.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const handleCreate = async () => {
    const name = newListName.trim();
    if (!name) return;
    setIsSaving(true);
    setCreateError(null);
    try {
      const detail = await createSharedList({ name, targetType: 'view', targetId: 'all' });
      // Navigate directly to the new list
      navigate(`/app/shared-lists/${detail.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create list.');
      setIsSaving(false);
    }
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void handleCreate();
    if (e.key === 'Escape') {
      setIsCreating(false);
      setNewListName('');
      setCreateError(null);
    }
  };

  const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <Layout breadcrumb="Shared Lists">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-text-tertiary-light dark:text-text-tertiary-dark" />
            <h1 className="text-lg font-semibold font-mono text-text-primary-light dark:text-text-primary-dark">
              Shared Lists
            </h1>
          </div>
          <button
            onClick={() => {
              setIsCreating(true);
              setNewListName('');
              setCreateError(null);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors"
          >
            <Plus className="w-4 h-4" />
            New list
          </button>
        </div>

        {/* Create new list inline form */}
        {isCreating && (
          <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-4 space-y-3">
            <label className="block text-xs font-mono text-text-tertiary-light dark:text-text-tertiary-dark uppercase tracking-wider">
              New list name
            </label>
            <input
              autoFocus
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              placeholder="e.g. Grocery run"
              className="w-full px-3 py-2 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {createError && (
              <p className="text-xs text-red-600 dark:text-red-400">{createError}</p>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => void handleCreate()}
                disabled={isSaving || !newListName.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded transition-colors"
              >
                {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isSaving ? 'Creating…' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewListName('');
                  setCreateError(null);
                }}
                className="px-3 py-1.5 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-text-tertiary-light dark:text-text-tertiary-dark" />
          </div>
        )}

        {/* Error state */}
        {!isLoading && error && (
          <div className="text-center py-16 space-y-2">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => void loadLists()}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && lists.length === 0 && !isCreating && (
          <div className="text-center py-16 space-y-3">
            <Users className="w-10 h-10 text-text-tertiary-light dark:text-text-tertiary-dark mx-auto opacity-40" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              No shared lists yet.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Create your first list
            </button>
          </div>
        )}

        {/* List items */}
        {!isLoading && !error && lists.length > 0 && (
          <ul className="space-y-2">
            {lists.map((list) => (
              <li key={list.id}>
                <button
                  onClick={() => navigate(`/app/shared-lists/${list.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-left"
                >
                  <Users className="w-4 h-4 shrink-0 text-text-tertiary-light dark:text-text-tertiary-dark" />
                  <div className="flex-1 min-w-0">
                    <span className="block font-mono text-sm font-medium text-text-primary-light dark:text-text-primary-dark truncate">
                      {list.name}
                    </span>
                    <span className="block text-xs text-text-tertiary-light dark:text-text-tertiary-dark mt-0.5">
                      {list.memberCount === 1
                        ? '1 member'
                        : `${list.memberCount} members`}{' '}
                      · {roleLabel(list.role)}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 shrink-0 text-text-tertiary-light dark:text-text-tertiary-dark" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
